import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { RoutingTabComponent } from '../../../components/ui/tabs/routing-tab/routing-tab.component';
import { CreateContactFormComponent } from '../../../components/forms/create-contact-form/create-contact-form.component';
import { CreateOrganizationFormComponent } from '../../../components/forms/create-organization-form/create-organization-form-modal.component';
import { CreateTagFormModalComponent } from '../../../components/forms/create-tag-form-modal/create-tag-form-modal.component';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { FeedbackCardComponent } from '../../../components/toasts-and-cards/feedback-card/feedback-card.component';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';

type ContactsTab = 'contacts' | 'organizations' | 'tags' | 'segments';

/** The four list tabs. Every other child route (details, import, builders) owns its whole page. */
const LIST_ROUTES: Record<string, ContactsTab> = {
  '/organization/contacts': 'contacts',
  '/organization/contacts/organizations': 'organizations',
  '/organization/contacts/tags': 'tags',
  '/organization/contacts/segmented-list': 'segments',
};

const TAB_COPY: Record<ContactsTab, { title: string; sub: string }> = {
  contacts: {
    title: 'Contacts',
    sub: 'Everyone you can message, with the organizations and tags they belong to.',
  },
  organizations: {
    title: 'Organizations',
    sub: 'Companies and groups your contacts belong to.',
  },
  tags: {
    title: 'Tags',
    sub: 'Labels for grouping contacts you message together.',
  },
  segments: {
    title: 'Segments',
    sub: 'Saved rules that pick contacts as your list changes.',
  },
};

@Component({
  selector: 'app-contacts',
  standalone: true,
  imports: [
    RouterOutlet,
    NgClickOutsideDirective,
    RoutingTabComponent,
    CreateContactFormComponent,
    CreateOrganizationFormComponent,
    CreateTagFormModalComponent,
    FeedbackCardComponent,
    HasPermissionDirective,
  ],
  templateUrl: './contacts.component.html',
  styleUrl: './contacts.component.css',
})
export class ContactsComponent implements OnInit {
  router = inject(Router);
  contactService = inject(ContactsService);
  private destroyRef = inject(DestroyRef);
  private document = inject(DOCUMENT);

  @ViewChild('addTrigger') addTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('addMenu') addMenu?: ElementRef<HTMLElement>;

  /** Which list tab is showing, or null on a full-page child (details, import, builders). */
  activeTab: ContactsTab | null = 'contacts';
  contactsCount: number = 0;

  showAddContactDropDown: boolean = false;

  // MODALS
  showAddSingleContactModal: boolean = false;
  showAddOrganizationModal: boolean = false;
  showAddTag: boolean = false;

  tabs = [
    { title: 'Contacts', count: 0, link: '/organization/contacts' },
    { title: 'Organizations', count: 0, link: '/organization/contacts/organizations' },
    { title: 'Tags', count: 0, link: '/organization/contacts/tags' },
    { title: 'Segments', count: 0, link: '/organization/contacts/segmented-list' },
  ];

  get title(): string {
    return this.activeTab ? TAB_COPY[this.activeTab].title : '';
  }

  get description(): string {
    return this.activeTab ? TAB_COPY[this.activeTab].sub : '';
  }

  // ── Add contacts menu ──
  toggleContactDropDown(event?: Event) {
    this.showAddContactDropDown = !this.showAddContactDropDown;
    // Enter or Space clicks report detail 0: carry keyboard focus into the menu.
    if (this.showAddContactDropDown && (event as MouseEvent | undefined)?.detail === 0) {
      this.focusAddItem(0);
    }
  }
  closeContactDropDown() {
    this.showAddContactDropDown = false;
  }
  onClickedOutside(_e: Event) {
    this.closeContactDropDown();
  }

  /** Arrow keys on the button open the menu at its first or last item. */
  onAddTriggerKeydown(event: Event) {
    const { key } = event as KeyboardEvent;
    if (key !== 'ArrowDown' && key !== 'ArrowUp') return;
    event.preventDefault();
    this.showAddContactDropDown = true;
    this.focusAddItem(key === 'ArrowUp' ? -1 : 0);
  }

  /** Arrow, Home and End move through the menu; Tab closes it. */
  onAddMenuKeydown(event: Event) {
    const { key } = event as KeyboardEvent;
    const items = this.addItems();
    if (!items.length) return;
    if (key === 'Tab') {
      this.addTrigger?.nativeElement.focus();
      this.closeContactDropDown();
      return;
    }
    const current = items.indexOf(event.target as HTMLElement);
    let next: number;
    switch (key) {
      case 'ArrowDown':
        next = current >= items.length - 1 ? 0 : current + 1;
        break;
      case 'ArrowUp':
        next = current <= 0 ? items.length - 1 : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    items[next].focus();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (!this.showAddContactDropDown) return;
    const focused = this.document.activeElement;
    const focusWasInMenu = !!focused && !!this.addMenu?.nativeElement.contains(focused);
    this.closeContactDropDown();
    if (focusWasInMenu) this.addTrigger?.nativeElement.focus();
  }

  private addItems(): HTMLElement[] {
    const menu = this.addMenu?.nativeElement;
    return menu ? Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]')) : [];
  }

  private focusAddItem(index: number) {
    setTimeout(() => {
      const items = this.addItems();
      items[index < 0 ? items.length + index : index]?.focus();
    });
  }

  OpenAddSingleContact() {
    this.closeContactDropDown();
    this.showAddSingleContactModal = !this.showAddSingleContactModal;
  }
  closeSingleContactModal() {
    this.showAddSingleContactModal = false;
  }
  // organization modal controls
  openCreateOrganizationModal() {
    this.showAddOrganizationModal = true;
  }
  closeCreateOrganizationModal() {
    this.showAddOrganizationModal = false;
  }
  // tag modal controls
  openCreateTagModal() {
    this.showAddTag = true;
  }
  closeCreateTagModal() {
    this.showAddTag = false;
  }

  // go to the multiple contacts page
  goToCreateMultipleContactsPage() {
    this.closeContactDropDown();
    this.router.navigate(['/organization/contacts/multiple-contacts']);
  }

  //go to the csv upload page
  goToCsvUpload() {
    this.closeContactDropDown();
    this.router.navigate(['/organization/contacts/csv']);
  }

  // go to the create segment page
  goToCreateSegmentPage() {
    this.closeContactDropDown();
    this.router.navigate(['/organization/contacts/segmented-list/create']);
  }

  /** Reads the tab from the path alone, so query strings never change the page. */
  private updateActiveTab(url: string): void {
    const path = (url || '').split(/[?#]/)[0].replace(/\/+$/, '');
    this.activeTab = LIST_ROUTES[path] ?? null;
    this.closeContactDropDown();
  }

  private setCount(index: number, count: number) {
    this.tabs = this.tabs.map((tab, i) => (i === index ? { ...tab, count: Number(count) || 0 } : tab));
  }

  ngOnInit() {
    this.updateActiveTab(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => this.updateActiveTab(event.urlAfterRedirects));

    // Tab counts once per visit. The contact list fetches its own page with
    // its sort and filters, so the shell no longer requests the list too.
    this.contactService.fetchContactsCount();
    this.contactService.fetchOrganizationCount();
    this.contactService.fetchTagsCount();
    this.contactService.fetchSegmentsCount();

    this.contactService.getContactLength
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count: number) => {
        this.contactsCount = Number(count) || 0;
        this.setCount(0, count);
      });
    this.contactService.getOrganizationsCount
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count: number) => this.setCount(1, count));
    this.contactService.getTagsLength
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count: number) => this.setCount(2, count));
    this.contactService.getSegmentsCount
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count: number) => this.setCount(3, count));
  }
}
